import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import { get } from "@/lib/api";
import { fmtDateTime, timeAgo } from "@/lib/format";
import { PageHeader, RunBadge } from "@/admin/ui";
import { RunLog } from "@/admin/RunProgress";

const duration = (r) => {
  if (!r.finished_at) return "";
  const s = Math.round((new Date(r.finished_at) - new Date(r.started_at)) / 1000);
  return s < 60 ? `${s} s` : `${Math.floor(s / 60)} min ${s % 60} s`;
};

const RunRow = ({ r }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border last:border-0" data-testid={`run-row-${r.id}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex flex-wrap items-center gap-4 px-5 py-4 text-left hover:bg-muted/40" data-testid={`run-toggle-${r.id}`}>
        <RunBadge status={r.status} />
        <div className="min-w-0 flex-1">
          <p className="font-medium truncate">{r.label}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{r.kind === "automation" ? "Automática" : "Manual"} · {fmtDateTime(r.started_at)} ({timeAgo(r.started_at)}){duration(r) && ` · ${duration(r)}`}</p>
        </div>
        <p className="text-sm text-muted-foreground hidden md:block max-w-xs truncate">{r.message}</p>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-3">
          <RunLog log={r.log} running={r.status === "running"} />
          {r.article_ids?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {r.article_ids.map((id, i) => <Link key={id} to={`/admin/noticias/${id}`} className="kicker text-[10px] text-primary border border-primary/30 px-2.5 py-1.5 hover:bg-primary/5" data-testid={`run-article-link-${id}`}>Noticia {i + 1} →</Link>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function Activity() {
  const { data, isLoading } = useQuery({ queryKey: ["runs"], queryFn: () => get("/admin/runs", { limit: 60 }), refetchInterval: 5000 });
  return (
    <div data-testid="admin-activity-page">
      <PageHeader kicker="Registro" title="Actividad" description="Todo lo que han hecho las automatizaciones y la IA, paso a paso." />
      <div className="bg-card border border-border">
        {isLoading && <p className="p-6 text-sm text-muted-foreground">Cargando…</p>}
        {data?.map((r) => <RunRow key={r.id} r={r} />)}
        {data && !data.length && <p className="p-10 text-center text-muted-foreground" data-testid="activity-empty">Sin actividad todavía.</p>}
      </div>
    </div>
  );
}
